import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Baby, Briefcase, Car, CheckCircle2, Clock, CreditCard, Award, Heart, Hotel,
  Luggage, Plane, ShieldCheck, Star, TrainFront, Users, Wifi,
} from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { BookingWidget } from "../components/BookingWidget";
import { ArticleCard } from "./Blog";
import { useLang } from "../i18n";

const IMG = {
  hero: "https://images.unsplash.com/photo-1620847969788-5326713ecb89?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  nice: "https://images.unsplash.com/photo-1643028010158-fb41f2d91ce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  resort: "https://images.pexels.com/photos/33353497/pexels-photo-33353497.jpeg?auto=compress&cs=tinysrgb&w=1200",
  door: "https://images.pexels.com/photos/8425027/pexels-photo-8425027.jpeg?auto=compress&cs=tinysrgb&w=1200",
  driver: "https://images.unsplash.com/photo-1627285886624-5cd637dafb50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  berline: "https://images.unsplash.com/photo-1661589997089-405a6076fc18?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  interior: "https://images.unsplash.com/photo-1669221721680-e13fcd462499?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  monaco: "https://images.unsplash.com/photo-1546448935-9d0f92876a03?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

const SERVICE_IMGS = [IMG.nice, IMG.resort, IMG.door, IMG.driver];
const SERVICE_ICONS = [Plane, Briefcase, Hotel, Clock];
const FLEET_IMGS = { berline: IMG.berline, van: IMG.interior };
const FLEET_SPEC_ICONS = [Users, Luggage, Wifi, Baby];

const Stars = ({ value, size = 16 }) => (
  <span className="flex gap-0.5" aria-label={`${value}/5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
    ))}
  </span>
);

export default function Home() {
  const { t, ta } = useLang();
  const [routes, setRoutes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const [cfg, setCfg] = useState({ google_review_url: "" });
  const [articles, setArticles] = useState([]);
  const [faqOpen, setFaqOpen] = useState(0);
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 700], [0, 140]);

  const services = ta("services.items");
  const fleetCards = ta("fleet.cards");
  const faqs = ta("faq.items");

  useEffect(() => {
    api.get("/routes/popular").then((r) => setRoutes(r.data)).catch(() => {});
    api.get("/reviews?limit=6").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/reviews/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/articles?limit=3").then((r) => setArticles(r.data)).catch(() => {});
    if (window.location.hash) {
      setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  useEffect(() => {
    const ld = {
      "@context": "https://schema.org", "@type": "LocalBusiness", name: "CabNow",
      description: "Chauffeur privé et VTC sur la Côte d'Azur : transferts aéroport Nice, gares, Cannes, Monaco, Saint-Tropez.",
      areaServed: ["Nice", "Cannes", "Monaco", "Antibes", "Saint-Tropez"],
      telephone: "+33661297599", priceRange: "€€",
      ...(summary.real_count > 0 && {
        aggregateRating: { "@type": "AggregateRating", ratingValue: summary.real_average, reviewCount: summary.real_count },
      }),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "cabnow-jsonld";
    s.text = JSON.stringify(ld);
    document.getElementById("cabnow-jsonld")?.remove();
    document.head.appendChild(s);
  }, [summary]);

  return (
    <div className="min-h-screen bg-sand" data-testid="home-page">
      <Navbar />

      {/* ---------------- HERO ---------------- */}
      <section className="grain relative overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: yBg }} className="absolute inset-0">
          <img src={IMG.hero} alt="CabNow — Côte d'Azur" className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-sand/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-sand via-sand/75 to-sand/10" />
          <div className="absolute inset-0 bg-gradient-to-b from-sand/50 via-transparent to-sand" />
        </motion.div>

        <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-4 pb-24 pt-36 sm:px-6 lg:grid-cols-12 lg:items-center lg:pb-32 lg:pt-44 lg:px-8">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="overline-label mb-6"
            >
              {t("hero.overline")}
            </motion.p>

            <h1 className="font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {[t("hero.l1"), t("hero.l2"), t("hero.l3")].map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 2 ? "italic text-azure" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              {summary.count > 0 && (
                <span className="flex items-center gap-2 rounded-full border border-gold/30 bg-gold-light px-4 py-2 text-sm font-semibold text-navy" data-testid="hero-rating-badge">
                  <Stars value={Math.round(summary.average)} size={14} />
                  {t("hero.rating", { avg: String(summary.average).replace(".", ","), count: summary.count })}
                </span>
              )}
              <span className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-navy">
                <ShieldCheck size={15} className="text-azure" /> {t("hero.badge1")}
              </span>
              <span className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-navy">
                <Plane size={15} className="text-azure" /> {t("hero.badge2")}
              </span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }}
            className="lg:col-span-5"
          >
            <BookingWidget />
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* ---------------- SERVICES ---------------- */}
      <section id="services" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="services-section">
        <Reveal>
          <p className="overline-label mb-4">{t("services.overline")}</p>
          <h2 className="max-w-2xl font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            {t("services.title")}
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          {services.map((s, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <Reveal key={s.title} delay={i * 0.08} className={i === 0 || i === 3 ? "md:col-span-7" : "md:col-span-5"}>
                <div
                  data-testid={`service-card-${i}`}
                  className="group relative h-72 overflow-hidden rounded-3xl border border-slate-100 shadow-sm transition-shadow duration-500 hover:shadow-2xl hover:shadow-navy/10"
                >
                  <img
                    src={SERVICE_IMGS[i]} alt={s.title} loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-8">
                    <Icon size={22} className="mb-3 text-gold" />
                    <h3 className="font-serif text-2xl font-semibold text-white">{s.title}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-white/75">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ---------------- ÉVÉNEMENTS & ENTREPRISES ---------------- */}
      <section id="evenements" className="bg-white" data-testid="events-section">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-28">
          <Reveal>
            <p className="overline-label mb-4">{t("events.overline")}</p>
            <h2 className="max-w-2xl font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              {t("events.title")}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-navy/60">{t("events.subtitle")}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:gap-8">
            {[
              { icon: Briefcase, title: t("events.biz_title"), text: t("events.biz_text"), tid: "events-business-card", sujet: "congres" },
              { icon: Heart, title: t("events.wed_title"), text: t("events.wed_text"), tid: "events-wedding-card", sujet: "mariage" },
            ].map((c, i) => (
              <Reveal key={c.tid} delay={i * 0.08} className="h-full">
                <div data-testid={c.tid} className="flex h-full flex-col rounded-3xl border border-slate-100 bg-sand p-8 shadow-sm sm:p-10">
                  <c.icon size={26} className="text-gold" />
                  <h3 className="mt-4 font-serif text-2xl font-semibold text-navy">{c.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-navy/65">{c.text}</p>
                  <Link
                    to={`/reservation?devis=1&sujet=${c.sujet}`}
                    data-testid={`${c.tid}-cta`}
                    className="group mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-azure px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-azure-hover"
                  >
                    {t("events.cta")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.12}>
            <div className="mt-12 rounded-3xl border border-slate-100 bg-white p-8 shadow-sm sm:p-10" data-testid="events-refs">
              <p className="overline-label mb-2">{t("events.refs_overline")}</p>
              <h3 className="font-serif text-xl font-semibold text-navy sm:text-2xl">{t("events.refs_title")}</h3>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {ta("events.refs_items").map((r, i) => (
                  <li key={i} data-testid={`events-ref-${i}`} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-sand px-5 py-4">
                    <Award size={18} className="shrink-0 text-gold" />
                    <span className="text-sm font-semibold text-navy/80">{r}</span>
                  </li>
                ))}
              </ul>
              <Link to="/partenaires" data-testid="events-refs-link"
                className="group mt-6 inline-flex items-center gap-2 text-sm font-bold text-azure hover:underline">
                {t("events.refs_link")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-xs text-navy/45">{t("events.note")}</p>
          </Reveal>
        </div>
      </section>
      <section id="tarifs" className="bg-white py-24 lg:py-32" data-testid="pricing-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="overline-label mb-4">{t("pricing.overline")}</p>
              <h2 className="font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
                {t("pricing.title")}
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-navy/55">{t("pricing.note")}</p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {routes.map((r, i) => (
              <Reveal key={`${r.from_id}-${r.to_id}`} delay={i * 0.06}>
                <div
                  data-testid={`route-card-${i}`}
                  className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-sand p-8 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-azure/40 hover:shadow-xl hover:shadow-azure/10"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-navy/45">{r.from_label}</p>
                      <div className="my-2 flex items-center gap-2 text-azure">
                        <span className="h-px w-8 bg-azure/40" />
                        <ArrowRight size={14} />
                        <span className="h-px w-8 bg-azure/40" />
                      </div>
                      <p className="font-serif text-xl font-semibold text-navy">{r.to_label}</p>
                    </div>
                    <span className="rounded-full bg-azure-soft px-3 py-1 text-xs font-bold text-azure">
                      ~{r.berline.duration_min} min
                    </span>
                  </div>

                  <div className="mt-6 space-y-2 border-t border-slate-200/80 pt-5">
                    <p className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-navy/60"><Car size={14} /> {t("pricing.berline")}</span>
                      <span className="font-serif text-xl font-semibold text-navy" data-testid={`route-berline-price-${i}`}>{r.berline.price_eur} €</span>
                    </p>
                    <p className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-navy/60"><Users size={14} /> {t("pricing.van")}</span>
                      <span className="font-serif text-xl font-semibold text-navy">{r.van.price_eur} €</span>
                    </p>
                  </div>

                  <Link
                    to={`/reservation?from=${r.from_id}&to=${r.to_id}`}
                    data-testid={`route-book-button-${i}`}
                    className="mt-auto pt-6"
                  >
                    <span className="flex w-full items-center justify-center gap-2 rounded-full border border-navy/15 py-3 text-sm font-bold text-navy transition-[background-color,color,border-color] group-hover:border-azure group-hover:bg-azure group-hover:text-white">
                      {t("pricing.book")} <ArrowRight size={15} />
                    </span>
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10 text-center">
            <p className="text-sm text-navy/50">
              {t("pricing.quote_pre")}{" "}
              <Link to="/reservation?devis=1" data-testid="pricing-quote-link" className="font-semibold text-azure hover:underline">
                {t("pricing.quote_link")}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------------- FLOTTE ---------------- */}
      <section id="flotte" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="fleet-section">
        <Reveal>
          <p className="overline-label mb-4">{t("fleet.overline")}</p>
          <h2 className="max-w-2xl font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            {t("fleet.title")}
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-8 md:grid-cols-2">
          {fleetCards.map((v, i) => (
            <Reveal key={v.id} delay={i * 0.1}>
              <div data-testid={`fleet-card-${v.id}`} className="group overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-shadow duration-500 hover:shadow-2xl hover:shadow-navy/10">
                <div className="relative h-72 overflow-hidden">
                  <img src={FLEET_IMGS[v.id]} alt={v.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-6 top-6 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold text-navy backdrop-blur">
                    {v.tag}
                  </span>
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl font-semibold text-navy">{v.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/60">{v.desc}</p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {v.specs.map((label, j) => {
                      const Icon = FLEET_SPEC_ICONS[j];
                      return (
                        <span key={label} className="flex items-center gap-2 rounded-xl bg-sand px-3 py-2.5 text-xs font-semibold text-navy/70">
                          <Icon size={14} className="text-azure" /> {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- BANDEAU IMAGE ---------------- */}
      <section className="relative h-[55vh] overflow-hidden" data-testid="riviera-banner">
        <img src={IMG.monaco} alt="Port de Monaco" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/45" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col items-start justify-center px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="overline-label mb-4 !text-gold">{t("banner.overline")}</p>
            <h2 className="max-w-xl font-serif text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
              {t("banner.title")}
            </h2>
          </Reveal>
        </div>
      </section>

      {/* ---------------- AVIS ---------------- */}
      <section id="avis" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="reviews-section">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="overline-label mb-4">{t("reviews.overline")}</p>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              {t("reviews.title")}
            </h2>
          </div>
          {summary.count > 0 && (
            <div className="flex items-center gap-4 rounded-2xl border border-gold/30 bg-gold-light px-6 py-4" data-testid="reviews-summary-card">
              <span className="font-serif text-4xl font-semibold text-navy">{String(summary.average).replace(".", ",")}</span>
              <div>
                <Stars value={Math.round(summary.average)} />
                <p className="mt-1 text-xs font-semibold text-navy/60">{t("reviews.collected", { count: summary.count })}</p>
              </div>
            </div>
          )}
        </Reveal>

        {reviews.length > 0 ? (
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.07}>
                <figure data-testid={`review-card-${i}`} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                  <Stars value={r.rating} />
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-navy/70">« {r.comment} »</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-azure font-serif text-lg font-semibold text-white">
                      {r.author_name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-bold text-navy">{r.author_name}</p>
                      <p className="text-xs text-navy/50">{r.route}</p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="mt-14 rounded-3xl border border-dashed border-slate-300 p-12 text-center text-sm text-navy/50" data-testid="reviews-empty">
            {t("reviews.empty")}
          </p>
        )}

        {cfg.google_review_url && (
          <Reveal className="mt-10 text-center">
            <a
              href={cfg.google_review_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-navy transition-[border-color,color] hover:border-azure hover:text-azure"
            >
              <Star size={15} className="fill-amber-400 text-amber-400" /> {t("reviews.google")}
            </a>
          </Reveal>
        )}
      </section>

      {/* ---------------- JOURNAL / SEO ---------------- */}
      {articles.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="home-articles-section">
          <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="overline-label mb-4">{t("blog.overline")}</p>
              <h2 className="font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
                {t("home.blog_title")}
              </h2>
            </div>
            <Link to="/blog" data-testid="home-blog-link" className="flex items-center gap-2 text-sm font-bold text-azure hover:underline">
              {t("blog.back")} <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {articles.slice(0, 3).map((a, i) => (
              <ArticleCard key={a.id} a={a} index={i} t={t} />
            ))}
          </div>
        </section>
      )}

      {/* ---------------- FAQ ---------------- */}
      <section className="bg-white py-24 lg:py-32" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="overline-label mb-4">{t("faq.overline")}</p>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              {t("faq.title")}
            </h2>
          </Reveal>

          <div className="mt-12 space-y-4">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-sand">
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-sm font-bold text-navy transition-colors hover:text-azure"
                  >
                    {f.q}
                    <span className={`font-serif text-2xl text-azure transition-transform duration-300 ${faqOpen === i ? "rotate-45" : ""}`}>+</span>
                  </button>
                  <div
                    className="grid transition-[grid-template-rows] duration-300 ease-out"
                    style={{ gridTemplateRows: faqOpen === i ? "1fr" : "0fr" }}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-6 text-sm leading-relaxed text-navy/60">{f.a}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <Reveal>
          <div className="grain relative overflow-hidden rounded-[2.5rem] bg-navy px-8 py-20 text-center sm:px-16" data-testid="cta-band">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-azure/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-gold/15 blur-3xl" />
            <p className="overline-label mb-4 !text-gold">{t("cta.overline")}</p>
            <h2 className="mx-auto max-w-2xl font-serif text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
              {t("cta.title")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base">
              {t("cta.text")}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/reservation"
                data-testid="cta-book-button"
                className="rounded-full bg-azure px-10 py-4 text-sm font-bold text-white shadow-lg shadow-azure/30 transition-[background-color,transform] hover:bg-azure-hover hover:-translate-y-0.5"
              >
                {t("cta.book")}
              </Link>
              <a
                data-testid="cta-call-button"
                className="rounded-full border border-white/25 px-10 py-4 text-sm font-bold text-white transition-colors hover:bg-white/10"
                href="tel:+33661297599"
              >
                +33 6 61 29 75 99
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs font-semibold text-white/50">
              <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-gold" /> {t("cta.trust1")}</span>
              <span className="flex items-center gap-2"><CreditCard size={14} className="text-gold" /> {t("cta.trust2")}</span>
              <span className="flex items-center gap-2"><TrainFront size={14} className="text-gold" /> {t("cta.trust3")}</span>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
